import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border border-border bg-surface p-5 text-text shadow-sm dark:shadow-none',
        className
      )}
      {...props}
    />
  )
);
Card.displayName = 'Card';

const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1 p-0', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-[17px] font-bold text-text', className)} {...props} />
  )
);
CardTitle.displayName = 'CardTitle';

const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-0', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mt-4 flex gap-2 p-0', className)} {...props} />
  )
);
CardFooter.displayName = 'CardFooter';

interface MetricCardProps extends HTMLAttributes<HTMLDivElement> {
  eyebrow: string;
  value: string;
  hint?: string;
}

export function MetricCard({ eyebrow, value, hint, className, ...props }: MetricCardProps) {
  return (
    <Card className={className} {...props}>
      <p className="text-eyebrow">{eyebrow}</p>
      <p className="mt-2 font-mono text-[30px] font-semibold tracking-tight text-text">{value}</p>
      {hint && <p className="mt-1 text-[13px] text-text-3">{hint}</p>}
    </Card>
  );
}

export { Card, CardHeader, CardTitle, CardContent, CardFooter };
